import { useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ACTIONS = {
  import: { label: "Will be invited", cls: "ss-pill-sky" },
  exists: { label: "Already in ScapeSource", cls: "ss-pill-green" },
  skip_external: { label: "External — skipped", cls: "ss-pill-gray" },
  skip_inactive: { label: "Not active in Ramp", cls: "ss-pill-gray" },
  skip_no_email: { label: "No email — skipped", cls: "ss-pill-gray" },
};
const ORDER = ["import", "exists", "skip_external", "skip_inactive", "skip_no_email"];

// 2026-10-27 — pulls the company's internal Ramp users into the Team as
// Purchasers. Preview first (nothing changes): accountants/bookkeepers,
// auditors and guests are external and skipped, and anyone already in
// ScapeSource is never duplicated (their Ramp account is just linked, so
// purchasers see only their own charges). Each new member gets the usual
// invite email and can also log non-Ramp expenses.
export default function RampUserImport({ onImported }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [importing, setImporting] = useState(false);

  const load = async () => {
    setPreview(null); setError(null);
    try {
      const { data } = await api.get("/contractor/integrations/ramp/users/preview");
      setPreview(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't read your users from Ramp");
    }
  };

  const importAll = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/contractor/integrations/ramp/users/import", {});
      toast.success(`${data.imported} invited${data.linked ? `, ${data.linked} existing linked to Ramp` : ""}`);
      setOpen(false);
      onImported?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const toImport = preview?.counts?.import || 0;
  const rows = preview ? [...preview.users].sort((a, b) => ORDER.indexOf(a.action) - ORDER.indexOf(b.action)) : [];

  return (
    <>
      <button onClick={() => { setOpen(true); load(); }} data-testid="import-ramp-users-button"
        className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-[13px] font-semibold">
        <Download size={14} /> Import from Ramp
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="ramp-users-dialog">
          <DialogHeader><DialogTitle>Import your Ramp users</DialogTitle></DialogHeader>
          <p className="text-[12.5px] text-slate-500">
            Internal Ramp users join your team as <b>Purchasers</b> and get an invite email. Accountants and other external Ramp users are skipped,
            and nobody already in ScapeSource is duplicated. You can change anyone's role afterwards.
          </p>
          {!preview && !error && <div className="py-6 text-center text-slate-400 text-sm flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Reading your Ramp users…</div>}
          {error && <div className="text-[12.5px] text-red-700 bg-red-50 border border-red-100 rounded-lg px-3.5 py-3" data-testid="ramp-users-error">{error}</div>}
          {preview && (
            <>
              <div className="max-h-72 overflow-y-auto border border-slate-100 rounded-lg" data-testid="ramp-users-list">
                {rows.length === 0 && <div className="p-4 text-center text-slate-400 text-[12.5px]">No users in Ramp.</div>}
                {rows.map((u) => (
                  <div key={u.ramp_user_id} className="flex items-center justify-between gap-3 px-3.5 py-2.5 border-b border-slate-50 last:border-0 text-[12.5px]" data-testid={`ramp-user-${u.ramp_user_id}`}>
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{u.name || u.email || "Unnamed"}</div>
                      <div className="text-slate-500 truncate">{u.email || "—"} · {(u.role || "").replace(/_/g, " ").toLowerCase()}</div>
                    </div>
                    <span className={`ss-pill ${ACTIONS[u.action]?.cls || "ss-pill-gray"} shrink-0`} title={u.reason}>{ACTIONS[u.action]?.label || u.action}</span>
                  </div>
                ))}
              </div>
              <button disabled={importing || toImport === 0} onClick={importAll} data-testid="confirm-import-ramp-users"
                className="w-full h-10 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold disabled:opacity-50">
                {importing ? "Importing…" : toImport === 0 ? "Nobody new to import" : `Invite ${toImport} ${toImport === 1 ? "person" : "people"}`}
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
